import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { DataTableFilterMeta, FilterMatchMode } from '../Imports';

export async function ExcelExport<T extends object>(
    data: T[],
    filters: DataTableFilterMeta,
    baseFileName: string
): Promise<void> {
    // Apply filters to the data
    const filteredData = data.filter((row) => {
        for (const key in filters) {
            const filterMeta = filters[key];
            if ('value' in filterMeta && 'matchMode' in filterMeta) {
                const filterMatchMode = filterMeta.matchMode;
                const filterValue = filterMeta.value;
                const fieldValue = row[key as keyof T];

                if (filterValue !== null && filterValue !== undefined && filterValue !== '') {
                    switch (filterMatchMode) {
                        case FilterMatchMode.CONTAINS:
                            if (!fieldValue?.toString().toLowerCase().includes(filterValue.toString().toLowerCase())) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.EQUALS:
                            if (fieldValue?.toString().toLowerCase() !== filterValue.toString().toLowerCase()) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.NOT_EQUALS:
                            if (fieldValue?.toString().toLowerCase() === filterValue.toString().toLowerCase()) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.STARTS_WITH:
                            if (!fieldValue?.toString().toLowerCase().startsWith(filterValue.toString().toLowerCase())) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.ENDS_WITH:
                            if (!fieldValue?.toString().toLowerCase().endsWith(filterValue.toString().toLowerCase())) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.LESS_THAN:
                            if (fieldValue >= filterValue) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.LESS_THAN_OR_EQUAL_TO:
                            if (fieldValue > filterValue) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.GREATER_THAN:
                            if (fieldValue <= filterValue) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.GREATER_THAN_OR_EQUAL_TO:
                            if (fieldValue < filterValue) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.IN:
                            if (!filterValue.includes(fieldValue)) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.BETWEEN:
                            if (!(fieldValue >= filterValue[0] && fieldValue <= filterValue[1])) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.DATE_IS:
                            if (new Date(fieldValue as string).toDateString() !== new Date(filterValue).toDateString()) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.DATE_IS_NOT:
                            if (new Date(fieldValue as string).toDateString() === new Date(filterValue).toDateString()) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.DATE_BEFORE:
                            if (new Date(fieldValue as string) >= new Date(filterValue)) {
                                return false;
                            }
                            break;
                        case FilterMatchMode.DATE_AFTER:
                            if (new Date(fieldValue as string) <= new Date(filterValue)) {
                                return false;
                            }
                            break;
                        default:
                            break;
                    }
                }
            }
        }
        return true;
    });

    const timestamp = new Date().toISOString().replace(/[-:.]/g, ''); // Removes special characters for a cleaner filename
    const fileName = `${baseFileName}_${timestamp}.xlsx`; // Example: "EXPORT_Reservations_20211007T123456.xlsx"

    try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Data');

        // Filter out columns with object or array values
        const filteredKeys = filteredData.length > 0
            ? Object.keys(filteredData[0]).filter((key) => {
                const value = filteredData[0][key as keyof T];
                return typeof value !== 'object' || value === null;
            })
            : [];

        // Add headers
        worksheet.addRow(filteredKeys);

        // Add data rows
        filteredData.forEach((row) => {
            const filteredRow = filteredKeys.map((key) => row[key as keyof T]);
            worksheet.addRow(filteredRow);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const dataBlob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
        });

        saveAs(dataBlob, fileName);
    } catch (error) {
        console.error('Error exporting file:', error);
    }
}
